import { apiClient } from '@/shared/api';
import type {
  KmAssessmentCreateRequest,
  KmAssessmentListResponse,
  KmAssessmentMoveRequest,
  KmAssessmentResponse,
  KmAssessmentUpdateRequest,
  KmContextAttributeRequest,
  KmContextLevel,
  KmContextResponse,
  KmEvalCreateRequest,
  KmEvalListResponse,
  KmEvalMoveRequest,
  KmEvalResponse,
  KmEvalUpdateRequest,
  KmGroupCreateRequest,
  KmGroupListResponse,
  KmGroupMoveRequest,
  KmGroupResponse,
  KmGroupUpdateRequest,
  KmNoteSetListResponse,
  KmNoteSetResponse,
  KmStructureParams,
  KmStructureResponse,
} from './types';

// GET /expert-report/km/structure — структура заключения целиком либо её ветка
export const fetchKmStructure = async (params: KmStructureParams): Promise<KmStructureResponse> => {
  const { data } = await apiClient.get<KmStructureResponse>('/expert-report/km/structure', { params });
  return data;
};

// GET /expert-report/km/group — группы одного уровня
export const fetchKmGroups = async (params: { reportId: number; parentId?: number }): Promise<KmGroupListResponse> => {
  const { data } = await apiClient.get<KmGroupListResponse>('/expert-report/km/group', { params });
  return data;
};

// GET /expert-report/km/group/{groupId} — карточка одной группы
export const fetchKmGroup = async (groupId: number): Promise<KmGroupResponse> => {
  const { data } = await apiClient.get<KmGroupResponse>(`/expert-report/km/group/${groupId}`);
  return data;
};

// POST /expert-report/km/group — создать группу
export const createKmGroup = async (body: KmGroupCreateRequest): Promise<KmGroupResponse> => {
  const { data } = await apiClient.post<KmGroupResponse>('/expert-report/km/group', body);
  return data;
};

// PUT /expert-report/km/group/{groupId} — переименовать группу или сменить признак заголовка
export const updateKmGroup = async (params: {
  groupId: number;
  body: KmGroupUpdateRequest;
}): Promise<KmGroupResponse> => {
  const { data } = await apiClient.put<KmGroupResponse>(`/expert-report/km/group/${params.groupId}`, params.body);
  return data;
};

// PUT /expert-report/km/group/{groupId}/move — перенести группу и/или сменить её порядок
export const moveKmGroup = async (params: { groupId: number; body: KmGroupMoveRequest }): Promise<KmGroupResponse> => {
  const { data } = await apiClient.put<KmGroupResponse>(`/expert-report/km/group/${params.groupId}/move`, params.body);
  return data;
};

// DELETE /expert-report/km/group/{groupId} — удалить группу со всем вложенным
export const deleteKmGroup = async (groupId: number): Promise<void> => {
  await apiClient.delete(`/expert-report/km/group/${groupId}`);
};

// GET /expert-report/km/assessment — единицы оценивания группы либо всего заключения
export const fetchKmAssessments = async (params: {
  reportId: number;
  groupId?: number;
}): Promise<KmAssessmentListResponse> => {
  const { data } = await apiClient.get<KmAssessmentListResponse>('/expert-report/km/assessment', { params });
  return data;
};

// GET /expert-report/km/assessment/{assessmentId} — карточка единицы оценивания
export const fetchKmAssessment = async (assessmentId: number): Promise<KmAssessmentResponse> => {
  const { data } = await apiClient.get<KmAssessmentResponse>(`/expert-report/km/assessment/${assessmentId}`);
  return data;
};

// POST /expert-report/km/assessment — создать единицу оценивания
export const createKmAssessment = async (body: KmAssessmentCreateRequest): Promise<KmAssessmentResponse> => {
  const { data } = await apiClient.post<KmAssessmentResponse>('/expert-report/km/assessment', body);
  return data;
};

// PUT /expert-report/km/assessment/{assessmentId} — переименовать единицу оценивания
export const updateKmAssessment = async (params: {
  assessmentId: number;
  body: KmAssessmentUpdateRequest;
}): Promise<KmAssessmentResponse> => {
  const { data } = await apiClient.put<KmAssessmentResponse>(
    `/expert-report/km/assessment/${params.assessmentId}`,
    params.body,
  );
  return data;
};

// PUT /expert-report/km/assessment/{assessmentId}/move — перенести единицу в другую группу и/или сменить порядок
export const moveKmAssessment = async (params: {
  assessmentId: number;
  body: KmAssessmentMoveRequest;
}): Promise<KmAssessmentResponse> => {
  const { data } = await apiClient.put<KmAssessmentResponse>(
    `/expert-report/km/assessment/${params.assessmentId}/move`,
    params.body,
  );
  return data;
};

// DELETE /expert-report/km/assessment/{assessmentId} — удалить единицу оценивания
export const deleteKmAssessment = async (assessmentId: number): Promise<void> => {
  await apiClient.delete(`/expert-report/km/assessment/${assessmentId}`);
};

// GET /expert-report/km/eval — определения оценки единицы либо всего заключения
export const fetchKmEvals = async (params: {
  reportId: number;
  assessmentId?: number;
}): Promise<KmEvalListResponse> => {
  const { data } = await apiClient.get<KmEvalListResponse>('/expert-report/km/eval', { params });
  return data;
};

// GET /expert-report/km/eval/{evalId} — карточка одного определения оценки
export const fetchKmEval = async (evalId: number): Promise<KmEvalResponse> => {
  const { data } = await apiClient.get<KmEvalResponse>(`/expert-report/km/eval/${evalId}`);
  return data;
};

// POST /expert-report/km/eval — создать определение оценки
export const createKmEval = async (body: KmEvalCreateRequest): Promise<KmEvalResponse> => {
  const { data } = await apiClient.post<KmEvalResponse>('/expert-report/km/eval', body);
  return data;
};

// PUT /expert-report/km/eval/{evalId} — изменить название или текст определения оценки
export const updateKmEval = async (params: { evalId: number; body: KmEvalUpdateRequest }): Promise<KmEvalResponse> => {
  const { data } = await apiClient.put<KmEvalResponse>(`/expert-report/km/eval/${params.evalId}`, params.body);
  return data;
};

// PUT /expert-report/km/eval/{evalId}/move — перенести определение в другую единицу и/или сменить порядок
export const moveKmEval = async (params: { evalId: number; body: KmEvalMoveRequest }): Promise<KmEvalResponse> => {
  const { data } = await apiClient.put<KmEvalResponse>(`/expert-report/km/eval/${params.evalId}/move`, params.body);
  return data;
};

// DELETE /expert-report/km/eval/{evalId} — удалить определение оценки
export const deleteKmEval = async (evalId: number): Promise<void> => {
  await apiClient.delete(`/expert-report/km/eval/${evalId}`);
};

// GET /expert-report/km/eval/{evalId}/note-set — наборы знаний определения оценки
export const fetchKmEvalNoteSets = async (evalId: number): Promise<KmNoteSetListResponse> => {
  const { data } = await apiClient.get<KmNoteSetListResponse>(`/expert-report/km/eval/${evalId}/note-set`);
  return data;
};

// POST /expert-report/km/eval/{evalId}/note-set/{noteSetId} — привязать набор знаний
export const addKmEvalNoteSet = async (params: { evalId: number; noteSetId: number }): Promise<KmNoteSetResponse> => {
  const { data } = await apiClient.post<KmNoteSetResponse>(
    `/expert-report/km/eval/${params.evalId}/note-set/${params.noteSetId}`,
  );
  return data;
};

// DELETE /expert-report/km/eval/{evalId}/note-set/{noteSetId} — отвязать набор знаний
export const removeKmEvalNoteSet = async (params: { evalId: number; noteSetId: number }): Promise<void> => {
  await apiClient.delete(`/expert-report/km/eval/${params.evalId}/note-set/${params.noteSetId}`);
};

// GET /expert-report/km/context — контекст оценки уровня заключения: назначенное ему вместе с приходящим сверху
export const fetchKmContext = async (params: KmContextLevel): Promise<KmContextResponse> => {
  const { data } = await apiClient.get<KmContextResponse>('/expert-report/km/context', { params });
  return data;
};

// POST /expert-report/km/context — назначить атрибут контексту оценки уровня
export const addKmContextAttribute = async (body: KmContextAttributeRequest): Promise<KmContextResponse> => {
  const { data } = await apiClient.post<KmContextResponse>('/expert-report/km/context', body);
  return data;
};

// DELETE /expert-report/km/context — снять атрибут с контекста оценки уровня
export const removeKmContextAttribute = async (params: KmContextAttributeRequest): Promise<void> => {
  await apiClient.delete('/expert-report/km/context', { params });
};
